/**
 * Makes Shaka's replay controls deterministic after VOD completion.
 *
 * Shaka normally rewinds from its `player.isEnded()` state. That state can lag
 * the native `ended` event for a completed manifest, causing the first replay
 * click to be handled as a no-op. Intercept the replay click before Shaka and
 * restart the native media element in the same user gesture.
 */
export function attachReplayControl(container: HTMLElement, video: HTMLVideoElement): () => void {
  const onReplayClick = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const replayButton = target.closest<HTMLElement>('.shaka-play-button, .shaka-small-play-button');
    if (!replayButton || !container.contains(replayButton)) return;

    const isAtEnd =
      video.ended || (Number.isFinite(video.duration) && video.duration > 0 && video.currentTime >= video.duration);
    if (!isAtEnd) return;

    // Prevent Shaka's stale end-state handler from handling this click again.
    event.preventDefault();
    event.stopPropagation();
    video.currentTime = 0;
    void video.play().catch(() => undefined);
  };

  container.addEventListener('click', onReplayClick, true);
  return () => container.removeEventListener('click', onReplayClick, true);
}
